import { store } from '@shared/redux';
import { authActions } from '@shared/redux/ducks/auth';
import type { AuthTokenResponse } from '@shared/api/generated-api';
import { hardResetAuth } from './hard-reset-auth';

type RefreshFn = () => Promise<boolean>;

const CH = 'auth-channel-v1';
const LOCK_KEY = 'auth-refresh-lock';
const LOCK_TTL = 15000;

const bc: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CH) : null;

let waiting: Array<(ok: boolean) => void> = [];

function now() {
  return Date.now();
}

function acquireLock(): boolean {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    if (raw) {
      const { t } = JSON.parse(raw);
      if (now() - t < LOCK_TTL) return false;
    }
    localStorage.setItem(LOCK_KEY, JSON.stringify({ t: now() }));
    return true;
  } catch {
    return false;
  }
}

function releaseLock() {
  try {
    localStorage.removeItem(LOCK_KEY);
  } catch {}
}

function notifyAll(ok: boolean) {
  const list = waiting.slice();
  waiting = [];
  list.forEach((r) => r(ok));
}

bc?.addEventListener('message', (e) => {
  const msg = e.data;
  if (msg?.type === 'refresh:success') notifyAll(true);
  if (msg?.type === 'refresh:fail') notifyAll(false);
  if (msg?.type === 'refresh:tokens' && msg?.payload) {
    store.dispatch(authActions.setTokens(msg.payload as AuthTokenResponse));
    notifyAll(true);
  }
});

window.addEventListener('storage', (ev) => {
  if (ev.key === LOCK_KEY && ev.newValue === null) {
  }
});

export async function refreshAcrossTabs(doRefresh: RefreshFn): Promise<boolean> {
  if (acquireLock()) {
    try {
      bc?.postMessage({ type: 'refresh:start' });
      const ok = await doRefresh();

      bc?.postMessage({ type: ok ? 'refresh:success' : 'refresh:fail' });
      return ok;
    } finally {
      releaseLock();
    }
  }

  return await new Promise<boolean>((resolve) => {
    waiting.push(resolve);
    setTimeout(() => resolve(false), LOCK_TTL + 2000);
  });
}

export function broadcastLogout() {
  bc?.postMessage({ type: 'logout' });
}

export function broadcastHardLogout() {
  bc?.postMessage({ type: 'logout:hard' });
}

export function broadcastTokens(tokens: AuthTokenResponse) {
  bc?.postMessage({ type: 'refresh:tokens', payload: tokens });
}
bc?.addEventListener('message', (e) => {
  if (e.data?.type === 'logout') {
    store.dispatch(authActions.logout());
  }
  if (e.data?.type === 'logout:hard') {
    hardResetAuth();
  }
});
